import { useCallback, useEffect, useRef, useState } from "react";
import type { IAgoraRTCClient } from "agora-rtc-sdk-ng";
import {
  appendMessage,
  chatId,
  decodeChat,
  encodeChat,
  frameFits,
  sanitizeName,
  sanitizeText,
  historyToMessages,
  type ChatMessage,
  type ChatRole,
} from "rc-app/utils/guidedRoomChat";
import { recordGuidedRoomMessage, getGuidedRoomMessages } from "rc-app/api/guidedRoomActionsApi";

/* ── Chat, over the voice channel ──────────────────────────────────────
   The reasoning for riding Agora rather than the sync relay, and for
   every byte cap here, is in utils/guidedRoomChat.ts. This file is only
   the plumbing: attach to the client the voice hook already joined, turn
   frames into messages, and hand back a send function that cannot lie
   about whether it worked.

   ── TWO CASTS, AND WHY THEY'RE HERE ──
   agora-rtc-sdk-ng 4.18.2 implements `sendStreamMessage(payload,
   reliable)` and emits `stream-message`, but ships typings that declare
   neither. Read out of the shipped bundle to be sure of the signature —
   including the 1024-byte guard that throws rather than truncating.

   The casts are contained to the two lines below on purpose. If a later
   SDK declares these properly, delete the casts and nothing else moves.
   If a later SDK REMOVES them, `typeof send !== "function"` below turns
   the composer off with a reason instead of throwing into a click
   handler — which is the failure a buyer would otherwise experience as
   a message that appeared to send and never arrived. */

type StreamCapableClient = IAgoraRTCClient & {
  sendStreamMessage?: (payload: Uint8Array | string, reliable?: boolean) => Promise<void>;
};

export type GuidedRoomChat = {
  messages: ChatMessage[];
  /** The composer is live. False while voice is still connecting, and on
      an SDK that can't carry a data stream. */
  ready: boolean;
  /** Why chat is unavailable, in words a buyer can act on. "" when fine. */
  unavailable: string;
  /** Resolves true when the message went out. Never throws. */
  send: (text: string) => Promise<boolean>;
  /** Unread count since the caller last cleared it — the dock badge. */
  unread: number;
  markRead: () => void;
};

type Options = {
  client: IAgoraRTCClient | null;
  /** Our own participant id, so an arriving frame can be told from an echo. */
  sessionId: string;
  displayName: string;
  role: ChatRole;
  enabled?: boolean;
  /* ── KEEPING THE MESSAGE AFTER THE ROOM CLOSES ──
     The data stream above is the whole of the live experience and stores
     nothing. When this credential is supplied, each message we send is ALSO
     posted to the backend, where it joins the tour's voice transcript and
     the analysis of what the buyer was actually asking about.

     Optional because it is not what makes chat work: a room with no
     credential — or a backend that is down — still chats perfectly. See
     the note on the send below for why a failed write is silent. */
  credential?: { session_id: string; session_secret: string } | null;
  /** Required alongside `credential` — the room the message belongs to. */
  roomId?: string;
  /** The advisor's name. Restored host messages carry no display_name
      — the backend knows the host by token — so without this their past
      lines come back nameless. */
  hostName?: string;
};

export const useGuidedRoomChat = ({
  client,
  sessionId,
  displayName,
  role,
  enabled = true,
  credential,
  roomId,
  hostName,
}: Options): GuidedRoomChat => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unread, setUnread] = useState(0);
  const [unavailable, setUnavailable] = useState("");

  /* Both held in refs: they change with every keystroke elsewhere on the
     page, and neither is a reason to detach and reattach the listener. */
  const nameRef = useRef(displayName);
  const roleRef = useRef(role);
  useEffect(() => { nameRef.current = displayName; }, [displayName]);
  useEffect(() => { roleRef.current = role; }, [role]);
  const hostNameRef = useRef(hostName);
  useEffect(() => { hostNameRef.current = hostName; }, [hostName]);

  /* Distinguishes two messages sent inside the same millisecond, which is
     what a paste followed by a fast Enter looks like. */
  const seqRef = useRef(0);

  /* ── SEED FROM WHAT WAS ALREADY SAID ──
     Runs once per room, independently of the Agora client: a buyer should
     get the conversation back on a refresh whether or not voice has
     finished connecting. Merged through appendMessage rather than
     assigned, so anything that arrived live in the meantime survives and
     the two dedupe on id.

     Silent on failure, and deliberately so. This only decides whether the
     log starts full or empty; a buyer who cannot read history can still
     take part in the tour, and an error banner about it would be noise
     during a live presentation. */
  const seededRef = useRef("");
  useEffect(() => {
    const cred = credential;
    if (!roomId || !cred?.session_id || !cred?.session_secret) return undefined;
    if (seededRef.current === roomId) return undefined;
    seededRef.current = roomId;

    let cancelled = false;
    getGuidedRoomMessages(roomId, cred)
      .then((res: any) => {
        if (cancelled) return;
        const history = historyToMessages(res?.messages, { hostName: hostNameRef.current });
        if (!history.length) return;
        setMessages((prev) => history.reduce((acc, m) => appendMessage(acc, m), prev));
      })
      .catch(() => { /* the tour is unaffected — see above */ });
    return () => { cancelled = true; };
  }, [roomId, credential]);

  useEffect(() => {
    if (!enabled || !client) return undefined;

    const c = client as StreamCapableClient;
    if (typeof c.sendStreamMessage !== "function") {
      setUnavailable("Chat isn't supported by this browser's connection — use voice.");
      return undefined;
    }
    setUnavailable("");

    const onFrame = (_uid: unknown, payload: unknown) => {
      const m = decodeChat(payload);
      if (!m) return;
      /* Agora does not echo to the sender, so anything arriving with our
         own session id came from another tab signed in as us. Appending
         it is right; appendMessage dedupes it against the local echo by
         id if it's the same message coming back some other way. */
      setMessages((prev) => {
        const next = appendMessage(prev, m);
        if (next !== prev && m.sessionId !== sessionId) setUnread((n) => n + 1);
        return next;
      });
    };

    /* Cast for the same reason as above: the event isn't in the typings. */
    (c as any).on("stream-message", onFrame);
    return () => { (c as any).off("stream-message", onFrame); };
  }, [client, enabled, sessionId]);

  const send = useCallback(async (raw: string): Promise<boolean> => {
    const c = client as StreamCapableClient | null;
    if (!c || typeof c.sendStreamMessage !== "function") return false;

    const text = sanitizeText(raw);
    if (!text) return false;

    const at = Date.now();
    seqRef.current += 1;
    const message: ChatMessage = {
      id: chatId(sessionId, at, seqRef.current),
      sessionId,
      name: sanitizeName(nameRef.current),
      role: roleRef.current,
      text,
      at,
    };

    const frame = encodeChat(message);
    /* sanitizeText already fits the text to its budget, so this only ever
       catches an unusually long NAME pushing the envelope over. Dropping
       the name is better than dropping the message: everyone in the room
       can see the people list. */
    const payload = frameFits(frame)
      ? frame
      : encodeChat({ ...message, name: "" });
    if (!frameFits(payload)) return false;

    try {
      await c.sendStreamMessage(payload, true);
    } catch {
      /* The room is still fine — voice is a separate path — so this is
         reported as one message failing, not as chat going down. */
      return false;
    }

    /* Local echo AFTER the send resolves. Before it, a failed send leaves
       the sender looking at a message nobody else has. */
    setMessages((prev) => appendMessage(prev, message));

    /* ── AND KEEP IT ──
       Deliberately not awaited, and its failure is deliberately silent. The
       message is already delivered; this write only decides whether it still
       exists tomorrow, and a buyer must never see an error — or a delay — for
       something that already worked. The backend takes our role and name from
       the presence record, so nothing here can misrepresent who sent it. */
    if (credential?.session_id && roomId) {
      void recordGuidedRoomMessage(roomId, credential, {
        text: message.text,
        sent_at_ms: message.at,
        display_name: message.name,
      }).catch(() => { /* the tour is unaffected */ });
    }
    return true;
  }, [client, sessionId, credential, roomId]);

  const markRead = useCallback(() => setUnread(0), []);

  return {
    messages,
    ready: !!client && !unavailable,
    unavailable,
    send,
    unread,
    markRead,
  };
};

export default useGuidedRoomChat;
